/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ApiRequestRejectedInterface,
  RequestRejectedApiResponseInterface,
  RequestRejectedInitialProperties,
} from '../../interface/request-rejected-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import penaltyManagementApi from '../../utils/api/penalty-management';

export const fetchRequestRejected = createAsyncThunk<
  RequestRejectedApiResponseInterface, // Return type
  ApiRequestRejectedInterface
>(
  'request-rejected/fetchRequestRejected',
  async (data: ApiRequestRejectedInterface, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestRejected(
        removeEmptyKeys({ ...data }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchRequestRejectedFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'request-rejected/fetchRejectedFilters',
  async (_, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: RequestRejectedInitialProperties = {
  requestRejected: [],
  limit: DefaultApiLimit.limit,
  page: 0,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const requestRejectedSlice = createSlice({
  name: 'request-rejected',
  initialState,
  reducers: {
    /**
     * Update the approved filters
     * @param state
     * @param action
     */
    updateRejectedFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableRejectedFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function to change sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Function to handle async actions
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<RequestRejectedInitialProperties>) => {
    builder
      .addCase(fetchRequestRejected.pending, (state: RequestRejectedInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchRequestRejected.fulfilled,
        (
          state: RequestRejectedInitialProperties,
          action: PayloadAction<RequestRejectedApiResponseInterface>,
        ) => {
          state.loading = false;
          state.requestRejected = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchRequestRejected.rejected, (state: RequestRejectedInitialProperties) => {
        state.loading = false;
      });
    builder
      .addCase(fetchRequestRejectedFilters.pending, (state: RequestRejectedInitialProperties) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchRequestRejectedFilters.fulfilled,
        (
          state: RequestRejectedInitialProperties,
          action: PayloadAction<CommonFilterProperties[]>,
        ) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...(action.payload || [])];
        },
      )
      .addCase(fetchRequestRejectedFilters.rejected, (state: RequestRejectedInitialProperties) => {
        state.filterListLoading = false;
      });
  },
});
export const { updateRejectedFilters, disableRejectedFilterApplied, setSortData } =
  requestRejectedSlice.actions;
export default requestRejectedSlice.reducer;
